import {useAtom} from 'jotai';
import {authorizationAtom} from '@/store/authorization-atom';
import {useRouter} from 'next/navigation';
import {clearAllQuoteStores} from '@kidslabs/module-quote';
import {visitorSessionClient} from '@/lib/visitor-session/visitor-session.client';
import {
    appliedCouponAtom,
    cartIdAtom,
    cartItemsAtom,
    cartTotalAtom,
    currentCheckoutStepAtom,
    emailAtom,
    initialSelectedShippingMethodState,
    selectedShippingMethodAtom,
} from '@/store/cart';
import {clearShippingCartAddressAtom, selectedCustomerAddressAtom} from '@/store/cart-address';
import {clearCheckoutAtom, isPollingPaymentStatusAtom, orderNumberAfterPlaceAtom} from '@/store/checkout';
import {AuthEnum} from '../enum/auth';
import {WebStorage} from '@/_application/core/storage/web-storage';
import {customerStoreAPI} from '@kidslabs/module-customer';
import {clearAuthData as clearAuthStoreData, clearLoginSessionData} from '@kidslabs/module-auth';
import {signOut} from 'next-auth/react';
import {CheckoutStep, WEB_CART_KEY} from '@/lib/constants';
import {drawerAtom} from '@/store/drawer-atom';
import {searchQueryAtom} from '@/store/search-atom';
import {otpAtom, initialOtpState} from '@/_application/modules/auth/stores/atom';

export const useLogout = () => {
    const router = useRouter();
    const [, setAuthorized] = useAtom(authorizationAtom);
    const [, setCartTotal] = useAtom(cartTotalAtom);
    const [, setCartItems] = useAtom(cartItemsAtom);
    const [, setCartId] = useAtom(cartIdAtom);
    const [, setEmail] = useAtom(emailAtom);
    const [, setAppliedCoupon] = useAtom(appliedCouponAtom);
    const [, setSelectedShippingMethod] = useAtom(selectedShippingMethodAtom);
    const [, setCurrentCheckoutStep] = useAtom(currentCheckoutStepAtom);
    const [, resetShippingAddress] = useAtom(clearShippingCartAddressAtom);
    const [, setSelectedCustomerAddress] = useAtom(selectedCustomerAddressAtom);
    const [, clearCheckout] = useAtom(clearCheckoutAtom);
    const [, setIsPollingPaymentStatus] = useAtom(isPollingPaymentStatusAtom);
    const [, setOrderNumberAfterPlace] = useAtom(orderNumberAfterPlaceAtom);
    const [, setDrawer] = useAtom(drawerAtom);
    const [, setSearchQuery] = useAtom(searchQueryAtom);
    const [, setOtpState] = useAtom(otpAtom);

    return {
        doLogout: async () => {
            // ================================================================
            // 1. Clear Legend-State stores (module-level stores)
            // ================================================================

            // Auth store (Legend-State) - triggers useQuoteModuleLogout & useCheckoutModuleLogout observers
            clearAuthStoreData();

            // Auth login session data (OTP, identity auth, etc.)
            clearLoginSessionData();

            // Cart & Quote stores (cart, cartItemReference, checkoutCart,
            // cartItemReferenceSynchronization, cartItemReferenceSelectedLogics)
            clearAllQuoteStores();

            // Checkout store (Legend-State - module-checkout)
            try {
                const {checkoutStoreAPI} = await import('@kidslabs/module-checkout');
                checkoutStoreAPI.actions.resetCheckoutStore();
            } catch {
                // module-checkout may not be loaded
            }

            // Gift selection store (Legend-State - module-flash-sale)
            try {
                const {giftSelectionStoreAPI} = await import('@kidslabs/module-flash-sale');
                giftSelectionStoreAPI.actions.resetAllSessions();
            } catch {
                // module-flash-sale may not be loaded
            }

            // Customer store (Legend-State - module-customer)
            customerStoreAPI.actions.resetStore();

            // ================================================================
            // 2. Clear Jotai atoms (app-level state)
            // ================================================================

            // Authorization
            setAuthorized(false);

            // Cart atoms
            setCartTotal({
                grand_total: {value: 0},
                subtotal_excluding_tax: {value: 0},
            });
            setCartItems([]);
            setCartId('');
            setAppliedCoupon('');

            // Checkout atoms
            setEmail('');
            setSelectedShippingMethod(initialSelectedShippingMethodState);
            setCurrentCheckoutStep(CheckoutStep.shippingAddress);
            resetShippingAddress(undefined);
            setSelectedCustomerAddress(null);
            clearCheckout(undefined);
            setIsPollingPaymentStatus(false);
            setOrderNumberAfterPlace('');

            // UI atoms
            setDrawer({display: false, view: '', data: null});
            setSearchQuery('');

            // OTP atoms
            setOtpState(initialOtpState);

            // ================================================================
            // 3. Clear visitor session (Redis) — remove customer cart IDs
            // ================================================================
            // CRITICAL: Must be called BEFORE clearing localStorage/NextAuth
            // so the session cookie (kp_sid) is still available for the API call.
            await visitorSessionClient.notifyLogout();

            // ================================================================
            // 4. Clear localStorage / WebStorage
            // ================================================================
            WebStorage.removeItem(AuthEnum.TOKEN);
            WebStorage.removeItem(AuthEnum.ACCESS_TOKEN);
            localStorage.removeItem(WEB_CART_KEY);
            localStorage.removeItem('mage-cache-storage');

            // ================================================================
            // 5. Clear NextAuth session
            // ================================================================
            await signOut({redirect: false});

            // ================================================================
            // 6. Refresh the page to reflect logged-out state
            // ================================================================
            router.refresh();
        },
    };
};
